import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Fade from "@mui/material/Fade";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMemo } from "react";
import { CompanyMatchUploadResponse } from "../../../types/companyMatch";
import { TEXT } from "./constants";
import { FileUploadFilterMode } from "./types";

export const ProcessingState = ({
  uploadResult,
  mode = FileUploadFilterMode.DEFINE_WHITESPACE_FILTER,
}: {
  uploadResult: CompanyMatchUploadResponse | null;
  mode: FileUploadFilterMode;
}) => {
  const progress = useMemo(() => {
    if (!uploadResult) return 0;
    if (uploadResult.lastMatched) return 100;
    if (!uploadResult.totalChunks) return 0;

    const processedChunks = uploadResult.processedChunks || 0;

    return Math.min(
      Math.round((processedChunks / uploadResult.totalChunks) * 100),
      100
    );
  }, [uploadResult]);

  // Set title and description based on mode
  let alertTitle = TEXT.PROCESSING_STATE.ALERT_TITLE;
  let alertDescription = TEXT.PROCESSING_STATE.ALERT_DESCRIPTION;

  if (mode === FileUploadFilterMode.UPLOAD_ACCOUNT_FILTER) {
    alertTitle = TEXT.PROCESSING_STATE.UPLOAD_ACCOUNT.ALERT_TITLE;
    alertDescription = TEXT.PROCESSING_STATE.UPLOAD_ACCOUNT.ALERT_DESCRIPTION;
  }

  return (
    <Stack
      direction="column"
      spacing={2}
      justifyContent="center"
      alignItems="center"
    >
      {mode === FileUploadFilterMode.ICP_CUSTOMER_LIST ? null : (
        <Alert severity="success">
          {alertTitle}
          <Typography variant="body2" color="textSecondary">
            {alertDescription}
          </Typography>
        </Alert>
      )}
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        spacing={2}
        padding={2}
        width="100%"
      >
        <Stack sx={{ position: "relative" }} display="inline-flex">
          <Fade in={progress === 0} timeout={400}>
            <Box sx={{ position: progress === 0 ? "relative" : "absolute" }}>
              <CircularProgress
                variant="indeterminate"
                size={70}
                thickness={4}
              />
            </Box>
          </Fade>
          <Fade in={progress > 0} timeout={400}>
            <Box sx={{ position: progress > 0 ? "relative" : "absolute" }}>
              <CircularProgress
                variant="determinate"
                size={70}
                thickness={4}
                value={progress}
                sx={{ transition: "all 0.3s ease-in-out" }}
              />
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: "absolute",
                }}
              >
                <Typography
                  variant="h5"
                  component="div"
                  sx={{ color: "text.secondary" }}
                >{`${Math.round(progress)}%`}</Typography>
              </Stack>
            </Box>
          </Fade>
        </Stack>

        <Typography variant="caption" component="div" color="text.secondary">
          {TEXT.PROCESSING_STATE.PROCESSING_TEXT}
        </Typography>
      </Stack>
    </Stack>
  );
};
